import { initializeCylinder, renderCylinder } from "./cylinder";
import { initializeDepthFloat, renderDepthFloat } from "./depth-float";
import { initializeGrid, renderGrid } from "./grid";

const VARIANT_META = {
	grid: {
		label: "Grid",
		slug: "grid",
	},
	cylinder3d: {
		label: "3D Cylinder",
		slug: "cylinder-3d",
	},
	depthFloat: {
		label: "Depth Float",
		slug: "depth-float",
	},
};

export const TOPIC_CLOUD_VARIANT_OPTIONS = Object.entries(VARIANT_META).map(
	([value, meta]) => ({
		label: meta.label,
		value,
	}),
);

export const DEFAULT_TOPIC_CLOUD_VARIANTS = {
	grid: {},
	cylinder3d: {
		scrollSensitivity: 0.16,
	},
	depthFloat: {
		pointerStrength: 0.85,
	},
};

function clamp(value, min, max) {
	return Math.min(max, Math.max(min, value));
}

function seededUnit(seed, offset = 1) {
	const value = Math.sin(seed * (offset + 1) * 12.9898) * 43758.5453;
	return value - Math.floor(value);
}

function getCountWeight(item) {
	return clamp(Number(item.sizeWeight || 0.5), 0.25, 1);
}

function getSeed(item) {
	return Number(
		item.seed || hashTopicValue(item.slug || item.label || "topic"),
	);
}

function setNodeStyles(node, styles = {}) {
	Object.entries(styles).forEach(([property, value]) => {
		if (value === undefined || value === null || value === "") {
			return;
		}

		node.style.setProperty(property, String(value));
	});
}

function getCylinderPlacement(item, index, total) {
	const seed = getSeed(item);
	const rings = Math.min(3, Math.max(1, Math.ceil(total / 8)));
	const itemsPerRing = Math.max(1, Math.ceil(total / rings));
	const ringIndex = index % rings;
	const stepIndex = Math.floor(index / rings);
	const angle = stepIndex * (360 / itemsPerRing);
	const radius = 176 + Math.round(seededUnit(seed, 6) * 34);
	const y =
		(ringIndex - (rings - 1) / 2) * 72 + (seededUnit(seed, 2) - 0.5) * 16;
	const scale = clamp(
		0.9 + getCountWeight(item) * 0.2 + seededUnit(seed, 4) * 0.08,
		0.9,
		1.22,
	);

	return {
		angle: `${angle.toFixed(2)}deg`,
		radius: `${radius}px`,
		scale: scale.toFixed(3),
		y: `${y.toFixed(2)}px`,
	};
}

function getDepthPlacement(item, index, total) {
	const seed = getSeed(item);
	const columns = Math.max(3, Math.ceil(Math.sqrt(total * 1.8)));
	const rows = Math.max(2, Math.ceil(total / columns));
	const row = Math.floor(index / columns);
	const column = index % columns;
	const baseX = 10 + (column / Math.max(columns - 1, 1)) * 80;
	const baseY = 18 + (row / Math.max(rows - 1, 1)) * 58;
	const x = clamp(baseX + (seededUnit(seed, 1) - 0.5) * 10, 6, 94);
	const y = clamp(baseY + (seededUnit(seed, 2) - 0.5) * 12, 12, 86);
	const z = Math.round((seededUnit(seed, 3) * 2 - 1) * 64);
	const scale = clamp(
		0.82 + getCountWeight(item) * 0.26 + seededUnit(seed, 5) * 0.18,
		0.82,
		1.42,
	);

	return {
		scale: scale.toFixed(3),
		x: x.toFixed(2),
		y: y.toFixed(2),
		z: `${z}px`,
	};
}

function getItemProps(item, variant, index, total, isEditor) {
	const seed = getSeed(item);
	const variantSlug = getTopicCloudVariantSlug(variant);
	const props = {
		"aria-label": `View posts tagged ${item.label}`,
		className: `sui-topic-cloud-item sui-topic-cloud-item--${variantSlug}`,
		"data-index": String(index),
		"data-seed": String(seed),
	};

	if (isEditor) {
		props.type = "button";
	} else {
		props.href = item.url;
	}

	if (variant === "cylinder3d") {
		const placement = getCylinderPlacement(item, index, total);
		props.style = {
			"--topic-angle": placement.angle,
			"--topic-radius": placement.radius,
			"--topic-scale": placement.scale,
			"--topic-y": placement.y,
		};
	}

	if (variant === "depthFloat") {
		const placement = getDepthPlacement(item, index, total);
		props["data-base-x"] = placement.x;
		props["data-base-y"] = placement.y;
		props["data-base-z"] = placement.z.replace("px", "");
		props["data-scale"] = placement.scale;
		props.style = {
			"--topic-scale": placement.scale,
			"--topic-x": placement.x,
			"--topic-y": placement.y,
			"--topic-z": placement.z,
		};
	}

	return props;
}

function createTopicCloudItem(item, variant, index, total, isEditor) {
	const tagName = isEditor ? "button" : "a";
	const props = getItemProps(item, variant, index, total, isEditor);
	const itemNode = document.createElement(tagName);
	itemNode.className = props.className;

	Object.entries(props).forEach(([key, value]) => {
		if (
			key === "className" ||
			key === "style" ||
			value === undefined ||
			value === null ||
			value === ""
		) {
			return;
		}

		if (key === "href") {
			itemNode.href = value;
			return;
		}

		if (key === "type") {
			itemNode.type = value;
			return;
		}

		itemNode.setAttribute(key, value);
	});

	setNodeStyles(itemNode, props.style);

	const labelNode = document.createElement("span");
	labelNode.className = "sui-topic-cloud-item-label";
	labelNode.textContent = item.label;
	itemNode.appendChild(labelNode);

	return itemNode;
}

function renderVariant(items, variant, isEditor) {
	const renderItem = (item, index, total) =>
		createTopicCloudItem(item, variant, index, total, isEditor);

	if (variant === "cylinder3d") {
		return renderCylinder(items, renderItem);
	}

	if (variant === "depthFloat") {
		return renderDepthFloat(items, renderItem);
	}

	return renderGrid(items, renderItem);
}

export function normalizeTopicCloudVariant(variant = "grid") {
	return VARIANT_META[variant] ? variant : "grid";
}

export function getTopicCloudVariantSlug(variant = "grid") {
	const normalizedVariant = normalizeTopicCloudVariant(variant);
	return VARIANT_META[normalizedVariant]?.slug || VARIANT_META.grid.slug;
}

export function hashTopicValue(value = "") {
	let hash = 0;

	for (const char of String(value)) {
		hash = (hash << 5) - hash + char.charCodeAt(0);
		hash |= 0;
	}

	return Math.abs(hash) + 1;
}

export function normalizeTopicCloudSettings(variant, settings = {}) {
	return {
		...(DEFAULT_TOPIC_CLOUD_VARIANTS[variant] || {}),
		...(settings || {}),
	};
}

export function createTopicCloudNode({
	className = "",
	emptyText = "No trending topics found.",
	isEditor = false,
	items = [],
	settings = {},
	variant = "grid",
} = {}) {
	const normalizedVariant = normalizeTopicCloudVariant(variant);
	const variantSlug = getTopicCloudVariantSlug(normalizedVariant);
	const normalizedSettings = normalizeTopicCloudSettings(
		normalizedVariant,
		settings,
	);
	const cloudNode = document.createElement("div");
	cloudNode.className = [
		"sui-topic-cloud",
		`sui-topic-cloud--${variantSlug}`,
		className,
	]
		.filter(Boolean)
		.join(" ");
	cloudNode.dataset.suiTopicCloud = "true";
	cloudNode.dataset.variant = variantSlug;

	if (normalizedSettings.pointerStrength) {
		cloudNode.dataset.pointerStrength = String(
			normalizedSettings.pointerStrength,
		);
	}

	if (normalizedSettings.scrollSensitivity) {
		cloudNode.dataset.scrollSensitivity = String(
			normalizedSettings.scrollSensitivity,
		);
	}

	if (items.length) {
		cloudNode.appendChild(renderVariant(items, normalizedVariant, isEditor));
	} else {
		const emptyNode = document.createElement("p");
		emptyNode.className = "sui-topic-cloud-empty";
		emptyNode.textContent = emptyText;
		cloudNode.appendChild(emptyNode);
	}

	return cloudNode;
}

export function hydrateTopicCloudShells(root = document) {
	root.querySelectorAll("[data-sui-topic-cloud-shell]").forEach((shell) => {
		if (shell.dataset.hydrated === "true") {
			return;
		}

		const payloadNode = shell.querySelector(".sui-topic-cloud-payload");

		if (!payloadNode?.textContent) {
			return;
		}

		try {
			const payload = JSON.parse(payloadNode.textContent);
			const cloudNode = createTopicCloudNode({
				className: payload.className || "",
				emptyText: payload.emptyText,
				items: payload.items || [],
				settings: payload.settings || {},
				variant: payload.variant || "grid",
			});

			shell.replaceChildren(cloudNode);

			shell.dataset.hydrated = "true";
		} catch (error) {
			console.error("Failed to hydrate topic cloud shell", error);
		}
	});
}

export function initializeTopicClouds(root = document) {
	root.querySelectorAll("[data-sui-topic-cloud]").forEach((node) => {
		if (node.dataset.initialized === "true") {
			return;
		}

		node.dataset.initialized = "true";

		if (node.dataset.variant === VARIANT_META.cylinder3d.slug) {
			initializeCylinder(node, { clamp, getCountWeight });
		}

		if (node.dataset.variant === VARIANT_META.depthFloat.slug) {
			initializeDepthFloat(node, { clamp });
		}

		if (node.dataset.variant === VARIANT_META.grid.slug) {
			initializeGrid(node);
		}
	});
}
